import { consoleRoutes, type TenantNavEntry, type TenantNavLink } from "@sagot/constants";
import type { CommandItem } from "@sagot/ui";
import type { GuideModule, GuideTopic } from "../features/guide/content";

export interface NavModel {
  entries: TenantNavEntry[];
  links: TenantNavLink[];
  active: TenantNavLink | undefined;
}

const isLink = (entry: TenantNavEntry): entry is TenantNavLink => entry.kind === "link";

/** The label of a link: the workspace's own word when its vocabulary names one, else the translated label. */
export function navLabel(link: Pick<TenantNavLink, "i18nKey" | "displayLabel">, translate: (key: string) => string): string {
  return link.displayLabel ?? translate(link.i18nKey);
}

/** Agent pages need an agent; without one selected they open the agents list. */
export function hrefFor(link: TenantNavLink, ws: string, agentId: string | null): string {
  if (link.agentScoped && !agentId) return consoleRoutes.agents(ws);
  return link.to({ ws, agentId: agentId ?? "" });
}

/** Server-filtered entries + the link matching the current path (longest match wins). */
export function buildNavModel(entries: TenantNavEntry[], ws: string, agentId: string | null, pathname: string): NavModel {
  const links = entries.filter(isLink);
  const matches = links.filter((link) => {
    const href = hrefFor(link, ws, agentId);
    return pathname === href || pathname.startsWith(`${href}/`);
  });
  const active = matches.sort((a, b) => hrefFor(b, ws, agentId).length - hrefFor(a, ws, agentId).length)[0];
  return { entries, links, active };
}

export function toCommandItems(links: readonly TenantNavLink[], ws: string, agentId: string | null, label: (link: TenantNavLink) => string, group: string): CommandItem[] {
  return links.map((link) => ({ id: `nav:${link.id}`, label: label(link), icon: link.icon, href: hrefFor(link, ws, agentId), group }));
}

/** Command-palette entries for the User Guide topics: titles and keywords are searchable, module titles too. */
export function toGuideCommandItems(topics: readonly GuideTopic[], modules: readonly GuideModule[], ws: string, group: string): CommandItem[] {
  const moduleById = new Map(modules.map((module) => [module.id, module]));
  return topics.map((topic) => {
    const module = moduleById.get(topic.module);
    return {
      id: `guide:${topic.id}`,
      label: topic.title,
      icon: module?.icon ?? "book-open",
      href: consoleRoutes.guide(ws, topic.id),
      group,
      keywords: [...topic.keywords, ...(module ? [module.title] : [])],
    };
  });
}
